import React from 'react';
import { Circle, Marker, Tooltip } from 'react-leaflet';

const MIN_RADIUS = 5;

const normalizeKind = (kind) => {
  const normalized = `${kind ?? ''}`.toLowerCase().trim();
  if (normalized === 'start') return 'origin';
  if (normalized === 'end') return 'destination';
  return normalized || 'checkpoint';
};

const colorForKind = (kind) => {
  const normalized = normalizeKind(kind);
  if (normalized === 'origin') return '#d9534f';
  if (normalized === 'destination') return '#f0ad4e';
  return '#2e7be7';
};

/**
 * Render node radius circles (origin/destination/checkpoint) with draggable resize handles.
 */
export default function NodeRadiusLayer({
  nodes = [],
  visible = true,
  editable = false,
  handleIcon,
  metersPerDegreeLat,
  metersPerDegreeLng,
  onChangeRadius = () => {},
  onSelectNode = () => {},
}) {
  if (!visible || !Array.isArray(nodes) || !nodes.length) return null;

  return nodes.map((node, idx) => {
    const lat = Number(node?.lat);
    const lng = Number(node?.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

    const radiusRaw = Number(node?.radiusMeters);
    const radius =
      Number.isFinite(radiusRaw) && radiusRaw > 0 ? Math.max(MIN_RADIUS, radiusRaw) : null;
    if (!radius) return null;

    const bearingRaw = Number(node?.handleBearing);
    const bearing = Number.isFinite(bearingRaw) ? bearingRaw : 0;
    const kind = normalizeKind(node?.kind);
    const color = colorForKind(kind);
    const canEdit = Boolean(editable && node?.canEdit !== false);
    const lngFactor = metersPerDegreeLng(lat || 0) || metersPerDegreeLat;
    const deltaLat = (radius * Math.cos(bearing)) / metersPerDegreeLat;
    const deltaLng = lngFactor ? (radius * Math.sin(bearing)) / lngFactor : 0;
    const handleLatLng = [lat + deltaLat, lng + deltaLng];
    const key = node?.key || node?.id || `${kind}-${idx}`;
    const title =
      kind === 'origin'
        ? 'Origin radius'
        : kind === 'destination'
          ? 'Destination radius'
          : 'Radius';

    return (
      <React.Fragment key={`node-radius-${key}`}>
        <Circle
          center={[lat, lng]}
          radius={radius}
          pathOptions={{
            color,
            fillColor: color,
            fillOpacity: 0.12,
            weight: 1,
            dashArray: kind === 'destination' ? '6 6' : null,
          }}
          eventHandlers={{
            click: () => onSelectNode(node),
          }}
        >
          <Tooltip className="responder-tooltip" direction="top" offset={[0, -10]} opacity={0.85}>
            {title}
          </Tooltip>
        </Circle>
        {canEdit && handleIcon && (
          <Marker
            position={handleLatLng}
            draggable
            icon={handleIcon}
            eventHandlers={{
              drag: (e) => {
                const next = e?.target?.getLatLng?.();
                if (!next) return;
                const dLat = next.lat - lat;
                const dLng = next.lng - lng;
                const nextLngFactor = metersPerDegreeLng(lat || 0) || metersPerDegreeLat;
                const metersLat = dLat * metersPerDegreeLat;
                const metersLng = dLng * nextLngFactor;
                const nextRadius = Math.max(
                  MIN_RADIUS,
                  Math.round(Math.sqrt(metersLat * metersLat + metersLng * metersLng)),
                );
                const nextBearing = Math.atan2(metersLng, metersLat);
                onChangeRadius(node, nextRadius, nextBearing);
              },
              click: () => onSelectNode(node),
            }}
          >
            <Tooltip className="responder-tooltip" direction="top" offset={[0, -10]} opacity={0.8}>
              Drag to resize
            </Tooltip>
          </Marker>
        )}
      </React.Fragment>
    );
  });
}
